/**
 * VideoTrimStep - Select video portion to blur
 *
 * Step 1 in the video workflow (5-step process):
 * Upload -> Trim -> Configure -> Preview -> Download
 *
 * This component shows the video player with timeline for selecting
 * which portion of the video to process for face detection and blur.
 */
import React, { useState, useCallback } from 'react';
import VideoTrimmer from './VideoTrimmer';
import { formatDuration } from '../utils/videoUtils';

export default function VideoTrimStep({
  file,
  onFileChange,
  onShowToast,
}) {
  // Get values from file or defaults
  const maxBlurDuration = file?.maxBlurDuration || 60;
  const videoDuration = file?.metadata?.duration_seconds || 0;
  const initialStart = file?.trimRange?.start || 0;
  const initialEnd = file?.trimRange?.end || Math.min(maxBlurDuration, videoDuration);

  // Local state for trim range
  const [trimStart, setTrimStart] = useState(initialStart);
  const [trimEnd, setTrimEnd] = useState(initialEnd);

  // Handle trim change - update local state and file state
  const handleTrimChange = useCallback((start, end) => {
    setTrimStart(start);
    setTrimEnd(end);
    // Store in file state for the next step
    onFileChange(prev => ({
      ...prev,
      trimRange: { start, end }
    }));
  }, [onFileChange]);

  return (
    <div className="max-w-3xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Select Video Portion</h2>
        <p className="text-gray-400">
          Choose which part of the video to process (max {formatDuration(maxBlurDuration)})
        </p>
      </div>

      {/* Video Info Bar */}
      <div className="bg-gray-800/50 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {file?.thumbnail && (
              <img
                src={file.thumbnail}
                alt={file.name}
                className="w-16 h-12 object-cover rounded-lg"
              />
            )}
            <div>
              <h3 className="text-white font-medium">{file?.name}</h3>
              <p className="text-sm text-gray-400">
                {file?.metadata?.width}x{file?.metadata?.height} •{' '}
                {formatDuration(videoDuration)} •{' '}
                {file?.resolutionLabel || 'HD'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-emerald-400 font-medium">
              {formatDuration(trimEnd - trimStart)}
            </p>
            <p className="text-xs text-gray-400">selected</p>
          </div>
        </div>
      </div>

      {/* Video Trimmer */}
      <VideoTrimmer
        videoUrl={file?.preview}
        duration={videoDuration}
        maxBlurDuration={maxBlurDuration}
        onTrimChange={handleTrimChange}
        initialStart={trimStart}
        initialEnd={trimEnd}
      />

      {/* Instructions */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          After selecting the portion, click "Continue" to detect faces in the selected range.
        </p>
      </div>
    </div>
  );
}
